import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CHANNEL_KIND_LABEL,
  type CampaignChannelKind,
  type NoticeProgramRecord,
  type PortfolioConnectionPublic,
  type SetupEventBindingRecord,
  type SetupEventDetectorView,
} from '@workspace/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Switch } from '@/components/ui/switch';
import { useConfirm } from '@/contexts/confirmContext';
import { CONTACT_AGENT_PATH, noticeProgramNewPath } from '../api/campaignPaths';
import { contactAgentApi } from '../api/contactAgentApi';
import { contactAgentError } from '../api/contactAgentScope';
import {
  SETUP_EVENT_CHANNELS,
  avisoProgramsForChannel,
  boundProgramChannel,
  parseCampaignChannelKind,
  pickSetupEventConnections,
} from '../api/setupEventChannel';

const NONE_PROGRAM = '__none__';

type PanelProps = {
  tenantId: string;
  programs: NoticeProgramRecord[];
};

type ConnectionEvents = {
  connection: PortfolioConnectionPublic;
  detectors: SetupEventDetectorView[];
};

export function ContactAgentSetupEventsPanel({ tenantId, programs }: PanelProps) {
  const { confirm } = useConfirm();
  const [groups, setGroups] = useState<ConnectionEvents[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [busyKey, setBusyKey] = useState('');

  const reload = async () => {
    const [connections, agents] = await Promise.all([
      contactAgentApi.listPortfolioConnections(),
      contactAgentApi.listAgents().catch(() => []),
    ]);
    const setup = pickSetupEventConnections(connections, agents);
    const next: ConnectionEvents[] = [];
    for (const connection of setup) {
      const detectors = await contactAgentApi.listSetupEventDetectors(connection.id);
      next.push({ connection, detectors });
    }
    setGroups(next);
    setLoadError('');
  };

  useEffect(() => {
    let cancelled = false;
    setLoadError('');
    setLoaded(false);
    void reload()
      .then(() => {
        if (!cancelled) {
          setLoaded(true);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setLoadError(contactAgentError(err));
          setLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (!loaded) {
    return <EventsSkeleton />;
  }

  if (groups.length === 0) {
    if (loadError) {
      return <p className='text-destructive'>{loadError}</p>;
    }
    return (
      <p className='text-sm text-muted-foreground'>
        Conectá Setup en el{' '}
        <Link to={CONTACT_AGENT_PATH} className='underline'>
          agente de contacto
        </Link>{' '}
        para disparar avisos solos.
      </p>
    );
  }

  return (
    <div className='space-y-6'>
      <div>
        <h2 className='text-lg font-medium'>Eventos Setup</h2>
        <p className='text-sm text-muted-foreground'>
          En cada evento elegí el canal (WhatsApp, SMS o email) y el disparo de ese canal. La
          primera corrida no manda mensajes: cuenta coincidencias y marca desde cuándo mirar.
        </p>
        {loadError ? <p className='mt-2 text-sm text-destructive'>{loadError}</p> : null}
      </div>
      {groups.map(group => (
        <div key={group.connection.id} className='space-y-3'>
          {groups.length > 1 ? (
            <p className='text-sm font-medium'>{group.connection.name}</p>
          ) : null}
          <div className='grid gap-3 md:grid-cols-2'>
            {group.detectors.map(view => (
              <EventCard
                key={view.detector.id}
                connectionId={group.connection.id}
                view={view}
                programs={programs}
                busy={busyKey === `${group.connection.id}:${view.detector.id}`}
                onBusy={busy => setBusyKey(busy ? `${group.connection.id}:${view.detector.id}` : '')}
                onConfirm={confirm}
                onSaved={reload}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EventsSkeleton() {
  return (
    <div className='space-y-4' aria-busy='true' aria-label='Cargando eventos Setup'>
      <Skeleton className='h-6 w-40' />
      <div className='grid gap-3 md:grid-cols-2'>
        {['a', 'b', 'c', 'd'].map(key => (
          <Card key={key} className='rounded-xl'>
            <CardHeader className='space-y-2'>
              <Skeleton className='h-5 w-48' />
              <Skeleton className='h-4 w-full' />
            </CardHeader>
            <CardContent className='space-y-3'>
              <Skeleton className='h-10 w-full' />
              <Skeleton className='h-8 w-24' />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function EventCard({
  connectionId,
  view,
  programs,
  busy,
  onBusy,
  onConfirm,
  onSaved,
}: {
  connectionId: string;
  view: SetupEventDetectorView;
  programs: NoticeProgramRecord[];
  busy: boolean;
  onBusy: (busy: boolean) => void;
  onConfirm: (input: { title: string; message: string }) => Promise<boolean>;
  onSaved: () => Promise<void>;
}) {
  const [programId, setProgramId] = useState(view.binding?.programId ?? '');
  const [channelKind, setChannelKind] = useState<CampaignChannelKind>(
    boundProgramChannel(programs, view.binding?.programId),
  );
  const [cardError, setCardError] = useState('');
  const channelPrograms = avisoProgramsForChannel(programs, channelKind);
  const selected = channelPrograms.find(item => item.id === programId);

  useEffect(() => {
    setProgramId(view.binding?.programId ?? '');
    setChannelKind(boundProgramChannel(programs, view.binding?.programId));
  }, [programs, view.binding?.programId]);

  const save = async (enabled: boolean) => {
    if (!programId) {
      setCardError('Elegí un programa de disparo para este evento.');
      return;
    }
    if (enabled) {
      const ok = await onConfirm({
        title: 'Encender evento',
        message:
          'La primera corrida no manda mensajes. Cuenta coincidencias históricas y marca desde cuándo mirar. ¿Seguimos?',
      });
      if (!ok) {
        return;
      }
    } else if (view.binding?.primed) {
      const ok = await onConfirm({
        title: 'Apagar evento',
        message: 'No van a salir más avisos de este evento hasta que lo vuelvas a encender.',
      });
      if (!ok) {
        return;
      }
    }
    onBusy(true);
    setCardError('');
    try {
      await contactAgentApi.upsertSetupEventBinding(connectionId, view.detector.id, {
        enabled,
        programId,
      });
      await onSaved();
    } catch (err) {
      setCardError(contactAgentError(err));
    } finally {
      onBusy(false);
    }
  };

  const persistChannel = (nextKind: CampaignChannelKind) => {
    setChannelKind(nextKind);
    setCardError('');
    if (selected?.channelKind !== nextKind) {
      setProgramId('');
    }
  };

  const persistProgram = async (nextId: string) => {
    setProgramId(nextId);
    setCardError('');
    if (!view.binding) {
      return;
    }
    if (!nextId) {
      setCardError('Elegí un programa de disparo para este evento.');
      return;
    }
    onBusy(true);
    try {
      await contactAgentApi.upsertSetupEventBinding(connectionId, view.detector.id, {
        enabled: view.binding.enabled,
        programId: nextId,
      });
      await onSaved();
    } catch (err) {
      setCardError(contactAgentError(err));
    } finally {
      onBusy(false);
    }
  };

  const sweepNow = async () => {
    onBusy(true);
    setCardError('');
    try {
      await contactAgentApi.sweepSetupEventBinding(connectionId, view.detector.id);
      await onSaved();
    } catch (err) {
      setCardError(contactAgentError(err));
    } finally {
      onBusy(false);
    }
  };

  return (
    <Card className='rounded-xl'>
      <CardHeader className='space-y-2'>
        <div className='flex items-start justify-between gap-3'>
          <CardTitle className='text-base'>{view.detector.label}</CardTitle>
          <Switch
            checked={Boolean(view.binding?.enabled)}
            disabled={busy}
            onCheckedChange={checked => void save(checked)}
            aria-label={view.detector.label}
          />
        </div>
        <p className='text-sm text-muted-foreground'>{view.detector.description}</p>
      </CardHeader>
      <CardContent className='space-y-3'>
        <label className='block space-y-1 text-sm'>
          <span className='text-muted-foreground'>Canal</span>
          <Select
            value={channelKind}
            disabled={busy}
            onValueChange={value => {
              const next = parseCampaignChannelKind(value);
              if (next) {
                persistChannel(next);
              }
            }}
          >
            <SelectTrigger aria-label={`Canal para ${view.detector.label}`}>
              <SelectValue placeholder='Elegí un canal' />
            </SelectTrigger>
            <SelectContent>
              {SETUP_EVENT_CHANNELS.map(kind => (
                <SelectItem key={kind} value={kind}>
                  {CHANNEL_KIND_LABEL[kind]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        {channelPrograms.length > 0 ? (
          <label className='block space-y-1 text-sm'>
            <span className='text-muted-foreground'>Disparo</span>
            <Select
              value={programId || NONE_PROGRAM}
              disabled={busy}
              onValueChange={value => void persistProgram(value === NONE_PROGRAM ? '' : value)}
            >
              <SelectTrigger aria-label={`Disparo ${CHANNEL_KIND_LABEL[channelKind]} para ${view.detector.label}`}>
                <SelectValue placeholder='Elegí un disparo' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_PROGRAM}>Elegí un disparo</SelectItem>
                {channelPrograms.map(program => (
                  <SelectItem key={program.id} value={program.id}>
                    {program.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        ) : (
          <p className='text-sm text-muted-foreground'>
            No hay un disparo de {CHANNEL_KIND_LABEL[channelKind]}.{' '}
            <Link to={noticeProgramNewPath(channelKind)} className='underline'>
              Crear disparo por {CHANNEL_KIND_LABEL[channelKind]}
            </Link>
            .
          </p>
        )}
        {selected?.status === 'DRAFT' ? (
          <p className='text-sm text-warning'>Activá el programa para que salga.</p>
        ) : null}
        <div className='flex flex-wrap items-center gap-2'>
          <Badge variant='outline'>{statusLabel(view.binding)}</Badge>
          {view.binding?.lastSweepAt ? (
            <span className='text-xs text-muted-foreground'>
              {timeAgo(view.binding.lastSweepAt)}
              {sweepStatsSuffix(view.binding)}
            </span>
          ) : null}
        </div>
        {cardError ? <p className='text-sm text-destructive'>{cardError}</p> : null}
        {view.binding?.lastSweepError ? (
          <p className='text-sm text-destructive'>{view.binding.lastSweepError}</p>
        ) : null}
        {view.binding?.enabled ? (
          <Button type='button' size='sm' variant='ghost' disabled={busy} onClick={() => void sweepNow()}>
            {view.binding.primed ? 'Probar ahora' : 'Corrida en seco'}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

function sweepStatsSuffix(binding: SetupEventBindingRecord): string {
  const stats = binding.lastSweepStats;
  const found = typeof stats?.found === 'number' ? stats.found : null;
  if (found === null) {
    return '';
  }
  if (stats?.dryRun === true) {
    return `, ${String(found)} coincidencias`;
  }
  const sent = typeof stats?.sent === 'number' ? stats.sent : null;
  if (sent !== null && sent > 0) {
    return `, ${String(sent)} enviados`;
  }
  const queued = typeof stats?.queued === 'number' ? stats.queued : null;
  if (queued !== null) {
    return `, ${String(queued)} en cola`;
  }
  return `, ${String(found)} enviados`;
}

function statusLabel(binding: SetupEventBindingRecord | null): string {
  if (!binding || !binding.enabled) {
    return 'Apagado';
  }
  if (binding.lastSweepStatus === 'FAILED') {
    return 'Error';
  }
  if (!binding.primed) {
    return 'En seco';
  }
  if (binding.lastSweepStatus === 'RUNNING') {
    return 'Barriendo';
  }
  return 'Activo';
}

function timeAgo(iso: string): string {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60_000));
  if (mins < 1) {
    return 'Hace un momento';
  }
  if (mins < 60) {
    return `Hace ${mins} min`;
  }
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return `Hace ${hours} h`;
  }
  return `Hace ${Math.floor(hours / 24)} d`;
}
